/**
 * Currency formatting utilities
 */

/**
 * Get currency symbol for a currency code
 * Example: "EUR" -> "€", "GBP" -> "£", "USD" -> "$"
 */
export const getCurrencySymbol = (currency: string | null | undefined): string => {
  if (!currency) return "$";
  
  const symbolMap: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    CAD: "C$",
    AUD: "A$",
    JPY: "¥",
    CNY: "¥",
    INR: "₹",
    CHF: "CHF",
    SEK: "kr",
    NOK: "kr",
    DKK: "kr",
    PLN: "zł",
    RUB: "₽",
    BRL: "R$",
    MXN: "$",
    ZAR: "R",
    NZD: "NZ$",
    SGD: "S$",
    HKD: "HK$",
    KRW: "₩",
    TRY: "₺",
    THB: "฿"
  };
  
  return symbolMap[currency.toUpperCase()] || currency;
};

/**
 * Format a number with thousand separators
 * Example: 1999.99 -> "1,999.99"
 */
export const formatCurrency = (value: string | number | null | undefined): string => {
  if (value == null || value === "") return "";
  
  const numValue = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(numValue)) return "";

  // Split into integer and decimal parts
  const parts = numValue.toString().split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];

  // Add thousand separators to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine with decimal part if it exists
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * Format a price with currency symbol and thousand separators
 * Example: 1099, "USD" -> "$1,099"
 * Example: 160, "EUR" -> "€160"
 */
export const formatPrice = (
  price: string | number | null | undefined,
  currency: string | null | undefined = "USD"
): string => {
  if (price == null || price === "") return "";
  
  const formatted = formatCurrency(price);
  if (!formatted) return "";
  
  const symbol = getCurrencySymbol(currency);
  return `${symbol}${formatted}`;
};

/**
 * Parse a formatted currency string back to a number
 * Example: "1,999.99" -> 1999.99
 */
export const parseCurrency = (value: string): string => {
  // Remove all commas and keep only numbers and decimal point
  return value.replace(/,/g, "");
};
